import React from 'react';
import type { BookingDetail } from '../../../types';

interface BookingCancelDialogProps {
  isOpen: boolean;
  booking: BookingDetail;
  isCancelling: boolean;
  onCancel: () => void;
  onGoBack: () => void;
}

export const BookingCancelDialog: React.FC<BookingCancelDialogProps> = ({
  isOpen,
  booking,
  isCancelling,
  onCancel,
  onGoBack,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Cancel Booking?
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-gray-600">
            Are you sure you want to cancel this booking?
          </p>

          {/* Booking Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="font-semibold text-gray-900">
              {booking.client.name}
            </div>
            <div className="text-sm text-gray-700">
              {booking.service.name}
            </div>
            <div className="text-sm text-gray-600">
              {booking.date_formatted} at {booking.time_range}
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-xs text-red-800">
              This action cannot be undone. The client will be notified of the cancellation.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-3">
          <button
            onClick={onGoBack}
            disabled={isCancelling}
            className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Go Back
          </button>
          <button
            onClick={onCancel}
            disabled={isCancelling}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCancelling ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Cancelling...</span>
              </>
            ) : (
              <span>Yes, Cancel Booking</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
